/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Target } from '@fluentui/react/lib/Callout';
import { action, computed, observable } from 'mobx';
import { LuisId } from 'src/luis/types/Resource';
import { Label } from 'src/modules/labeling/types/Label';

/**
 * Reactive store encapsulate all the menu labeling UI states.
 */
export class MenuStore {
    @observable
    public currentUtteranceId: LuisId;

    @observable
    public target: Target;

    @observable.shallow
    public clickedOnLabel: Label;

    /**
     * Callback used by the labeler UI component to cancel the UI parentheses.
     */
    private _cancelSelectionCB: () => void;

    constructor() {
        this.currentUtteranceId = null;
        this.target = null;
        this.clickedOnLabel = null;
        this._cancelSelectionCB = null;
    }

    /**
     * Setter for the current utterance id.
     */
    @action
    public setCurrentUtteranceId(uid: LuisId) {
        this.currentUtteranceId = uid;
    }

    /**
     * Setter for the target dom element.
     */
    @action
    public setTarget(target: Target) {
        this.target = target;
    }

    /**
     * Setter for the labeler UI cancel selection callback.
     */
    public setCancelSelectionCallBackCB(cb: () => void) {
        this._cancelSelectionCB = cb;
    }

    /**
     * Resets the menu state to the default one.
     */
    @action
    public resetMenuState() {
        this._cancelSelectionCB?.();
        this.target = null;
        this.clickedOnLabel = null;
    }

    /**
     * Setter for the clicked on label.
     */
    @action
    public setClickedOnLabel(label: Label) {
        this.clickedOnLabel = label;
    }

    @computed
    public get isHidden() {
        return !this.target;
    }
}
